import React, { useContext, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { hideSlideOutPanel, modeUIContext } from '@moderepo/mode-ui-state';
import { BaseCompAction, capitalizeAll, SlideOutComp } from '@moderepo/mode-app-components';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { Grid } from '@material-ui/core';
import { useRitzTranslation } from '../../utils/customHooks';
import { SCRouteHelper } from './SCRouteHelper';
import { SCSensorModuleInfoContainer } from './SCSensorModuleInfoContainer';
import { SCSensorsTableContainer } from './SCSensorsTableContainer';
import { SensorModuleContainer } from './SCUtils';


interface SCSensorModulePreviewContainerProps {
    readonly projectId: number;
    readonly moduleId: string;

    // The container, 'home' or 'device', which the sensor modules belonged to and it's ID, 'homeId' or 'deviceId'.
    readonly smContainer: SensorModuleContainer;
    readonly smContainerId: number;

    readonly userId?: number | undefined;
    readonly homeId?: number | undefined;
}


/**
 * This Component is used for displaying HomeInfo on the preview panel for preview. It does not show all home details e.g. devices, logs,
 * kv pairs, etc... but only the Home object details
 */
export const SCSensorModulePreviewContainer: React.FC<SCSensorModulePreviewContainerProps> = (props: SCSensorModulePreviewContainerProps) => {
    const navigate = useNavigate();
    const { projectId } = props;
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const project = selectProjectById(dataState, projectId);
    const { trans } = useRitzTranslation(project);
    const scRouteHelper = SCRouteHelper.getInstance(navigate);



    /**
     * Take the user to the Sensor Module details page page
     */
    const viewSensorModuleDetails = useCallback(() => {
        if (props.smContainer === SensorModuleContainer.DEVICE) {
            scRouteHelper.gotoDeviceSensorModuleDetailsPage({
                projectId,
                moduleId: props.moduleId,
                deviceId: props.smContainerId,
                userId  : props.userId,
                homeId  : props.homeId,
            });
        } else {
            scRouteHelper.gotoHomeSensorModuleDetailsPage({
                projectId,
                moduleId: props.moduleId,
                homeId  : props.smContainerId,
                userId  : props.userId,
            });
        }
        uiDispatch(hideSlideOutPanel());

    }, [projectId, props.smContainer, props.smContainerId, props.homeId, props.moduleId, props.userId, scRouteHelper, uiDispatch]);



    const primaryActions = useMemo(() => {
        const actions: BaseCompAction[] = [{
            label  : capitalizeAll(trans('label.view_details')),
            onClick: viewSensorModuleDetails,
        }];
        return actions;
    }, [trans, viewSensorModuleDetails]);



    const slideOutCompContent = useMemo(() => {
        return (
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <SCSensorModuleInfoContainer
                        projectId={props.projectId}
                        smContainer={props.smContainer}
                        smContainerId={props.smContainerId}
                        moduleId={props.moduleId}
                    />
                </Grid>
                <Grid item xs={12}>
                    <SCSensorsTableContainer
                        projectId={props.projectId}
                        smContainer={props.smContainer}
                        smContainerId={props.smContainerId}
                        moduleId={props.moduleId}
                    />
                </Grid>
            </Grid>
        );
    }, [props.smContainer, props.smContainerId, props.moduleId, props.projectId]);



    return (
        <SlideOutComp
            primaryActions={primaryActions}
            compContent={slideOutCompContent}
            contentContainerProps={{
                className: 'slide-out-comp-container',
            }}
        />
    );
};
